import { toast } from "@/components/common/Toast"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { UseMutationResult } from "@tanstack/react-query"
import type { Dispatch, SetStateAction } from "react"
import { useState } from "react"
import { useSearchParams } from "react-router"
import { toastApiError } from "../../api/apiError"
import { mergeAdditionalAttributes } from "../shared/mergeAdditionalAttributes"

export interface EntityFieldsState<TOut, TUpdate> {
  entity: TOut | undefined
  isLoading: boolean
  editing: boolean
  setEditing: (editing: boolean) => void
  edits: TUpdate
  setEdits: Dispatch<SetStateAction<TUpdate>>
  isPending: boolean
  handleSave: () => void
  handleCancel: () => void
}

/**
 * Shared shell behind the Fields sections' `use*FieldsState` hooks (Articles,
 * Products, Suppliers, Customers) — reads `?id=`, loads the detail record,
 * and wires the edit/save/cancel flow with `additionalAttributes` merging on
 * save. Each entity's own hook is a thin wrapper renaming `entity` to its own
 * field name for its `*FieldsBody`'s convenience (see e.g. useArticleFieldsState).
 */
export function useEntityFieldsState<
  TOut extends {
    id: string
    additionalAttributes: Record<string, unknown> | null
  },
  TUpdate extends { additionalAttributes?: Record<string, unknown> },
>(
  useDetail: (id: string | null) => {
    data: TOut | undefined
    isLoading: boolean
  },
  usePatch: () => UseMutationResult<
    TOut,
    Error,
    { id: string; changes: TUpdate }
  >,
): EntityFieldsState<TOut, TUpdate> {
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")

  const { data: entity, isLoading } = useDetail(id)
  const { mutate: patch, isPending } = usePatch()

  const [editing, setEditing] = useState(false)
  const [edits, setEdits] = useState<TUpdate>({} as TUpdate)

  function handleSave() {
    if (!entity) return
    patch(
      {
        id: entity.id,
        changes: {
          ...edits,
          additionalAttributes: mergeAdditionalAttributes(
            entity.additionalAttributes ?? undefined,
            edits.additionalAttributes,
          ),
        } as TUpdate,
      },
      {
        onSuccess: () => {
          setEditing(false)
          setEdits({} as TUpdate)
          toast.success(translate("MC_MD_SAVE_SUCCESS"))
        },
        onError: (err) => {
          toastApiError(err, translate, "MC_MD_SAVE_ERROR")
        },
      },
    )
  }

  function handleCancel() {
    setEditing(false)
    setEdits({} as TUpdate)
  }

  return {
    entity,
    isLoading,
    editing,
    setEditing,
    edits,
    setEdits,
    isPending,
    handleSave,
    handleCancel,
  }
}
